import Backbone from 'backbone';
import { getShapes } from '@ardoq/icons';
import { ArrowType, LineType } from '@ardoq/api-types';
import { DEFAULT_REFERENCE_TYPES } from '@ardoq/data-model';
import { logError } from '@ardoq/logging';
import { getApiUrl } from 'backboneExtensions';
import type { Model as ModelBackboneModel } from 'aqTypes';
import type { ModelType } from './ModelType';
import { each } from 'lodash';

interface GetReferenceTypeByIdOptions {
  skipDeletedTypes?: boolean;
}

export { IMPLICIT_REFTYPE_ID } from '@ardoq/api-types';
export { DEFAULT_REFERENCE_TYPES } from '@ardoq/data-model';
type ModelClass = {
  extend: typeof Backbone.Model.extend;
  new (attributes?: Record<string, any>): ModelBackboneModel;
};
export const Model: ModelClass = Backbone.Model.extend({
  idAttribute: '_id',
  urlRoot: `${getApiUrl(Backbone.Model)}/api/model`,
  _internalModelChanged: 'internal:ModelChanged',
  _svgShapes: getShapes(),
  defaultReferenceTypes: DEFAULT_REFERENCE_TYPES,
  defaults: {
    name: '',
    category: 'Other',
    useAsTemplate: false,
    description: '',
    root: null,
    flexible: false,
    referenceTypes: null,
    blankTemplate: false,
    startView: null,
    defaultViews: [],
  },
  getReferenceTypes: function (this: ModelBackboneModel) {
    const referenceTypes = this.get('referenceTypes');
    if (!referenceTypes) {
      return this.defaultReferenceTypes;
    } else if (!referenceTypes[2]) {
      // Ensure that the implicit reference type is always present
      referenceTypes[2] = this.defaultReferenceTypes[2];
    }
    return referenceTypes;
  },
  getReferenceTypeById: function (
    this: ModelBackboneModel,
    id: string | number,
    options: GetReferenceTypeByIdOptions = {}
  ) {
    const referenceType = this.getReferenceTypes()[id];

    if (referenceType) {
      return referenceType;
    } else if (!options.skipDeletedTypes) {
      return {
        name: 'Error - deleted type',
        id,
        line: LineType.DOTTED,
        color: 'red',
        lineBeginning: ArrowType.NONE,
        lineEnding: ArrowType.NONE,
      };
    }
  },
  fixModel: function (this: ModelBackboneModel, children: ModelType) {
    const localChildren: Record<string, ModelType> = children
      ? children.children
      : this.attributes.root;
    if (!children) {
      this._flattenedModel = {};
      this._parentList = {};
    }
    let index = 1;
    each(localChildren, (model, key) => {
      if (this._flattenedModel![key]) {
        const errorString = 'Model already contains type of name';
        logError(new Error(errorString), errorString, { key });
      } else {
        this._parentList![key] = localChildren;
        this._flattenedModel![key] = model;
        if (model.index !== index) {
          model.index = index;
        }
        index++;
        this.fixModel(model);
      }
    });
  },
  initialize: function (this: ModelBackboneModel) {
    if (!this.get('root')) {
      this.set('root', {});
    }

    this.on(`${this._internalModelChanged} changed:root sync`, () => {
      this.fixModel();
    });
  },
  name: function (this: ModelBackboneModel) {
    return this.get('name');
  },
  toString: function (this: ModelBackboneModel) {
    return this.name();
  },
});
